import { DateRange } from "react-date-range";
import { format } from "date-fns";
import { useState, useRef, useEffect } from "react";
import { Calendar } from "lucide-react";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const OrderDateFilter = ({ fromDate, toDate, onApply, onClear, className }) => {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);

  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  /* ---------------- CLOSE ON OUTSIDE CLICK ---------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------- APPLY ---------------- */
  const handleApply = () => {
    onApply(
      format(range[0].startDate, "yyyy-MM-dd"),
      format(range[0].endDate, "yyyy-MM-dd")
    );
    setOpen(false);
  };

  /* ---------------- CLEAR ---------------- */
  const handleClear = () => {
    onClear();
    setOpen(false);
  };

  /* ---------------- LABEL ---------------- */
  let label = "Select dates";

  if (fromDate && toDate) {
    const start = new Date(fromDate);
    const end = new Date(toDate);

    const sameDay = start.toDateString() === end.toDateString();
    const sameYear = start.getFullYear() === end.getFullYear();

    if (sameDay) {
      label = format(start, "dd MMM yyyy");
    } else if (sameYear) {
      label = `${format(start, "dd MMM")} – ${format(end, "dd MMM yyyy")}`;
    } else {
      label = `${format(start, "dd MMM yyyy")} – ${format(end, "dd MMM yyyy")}`;
    }
  }

  return (
    <div ref={wrapperRef} className="relative w-full sm:w-auto">
      {/* BUTTON */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={`${className || "w-full sm:w-60 px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 bg-white text-sm text-slate-800 outline-none transition"} flex items-center justify-start sm:justify-center gap-2`}
      >
        <Calendar size={14} className="text-gray-500 shrink-0" />
        <span className="truncate whitespace-nowrap">
          {label}
        </span>
      </button>

      {/* CALENDAR DROPDOWN */}
      {open && (
        <div className="absolute right-0 mt-2 z-50 bg-white rounded-xl shadow-xl border w-[340px]">
          <div className="p-3">
            <DateRange
              ranges={range}
              onChange={(item) => {
                const newRange = item.selection;
                setRange([newRange]);
                // Automatically push to parent so main Search button can see it
                onApply(
                  format(newRange.startDate, "yyyy-MM-dd"),
                  format(newRange.endDate, "yyyy-MM-dd")
                );
              }}
              moveRangeOnFirstSelection={false}
              editableDateInputs={false}
              months={1}
              direction="horizontal"
              showDateDisplay={false}
              rangeColors={["#172554"]} 
            />
          </div>

          <div className="flex gap-2 p-3 border-t bg-gray-50 rounded-b-xl">
            <button
              onClick={() => setOpen(false)}
              className="flex-1 bg-[#172554] hover:bg-[#1e2f6a]
                         text-white py-1.5 rounded-md text-xs font-medium transition"
            >
              Done
            </button>

            <button
              onClick={handleClear}
              className="flex-1 bg-white border border-slate-300 hover:bg-gray-100
                         text-gray-700 py-1.5 rounded-md text-xs font-medium transition"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDateFilter;